import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { EMPTY, Observable, distinctUntilChanged, filter, map, merge, of, startWith, switchMap } from 'rxjs';
import { PersonaService, ResolvedPersona } from 'app/core/auth/persona.service';
import { Persona, homeFor } from 'app/core/auth/persona-mapping';
import { AuthService } from 'app/core/auth/auth.service';
import { WorkspaceService } from 'app/shared/services/workspace/workspace.service';
import { planOffersCro } from 'app/shared/services/workspace/persona-plans';

/** What a route requires. */
export type PersonaRequirement = 'admin' | 'superadmin' | 'is' | 'cro';

/**
 * SPEC-web-planner-007 AC-17 (B10, C11, OQ-2). Applied only in app.routing.ts.
 *  - route.data.persona set  -> that requirement for the route;
 *  - otherwise (canActivateChild on the stubs mount) -> admin when the MATCHED
 *    route's first path segment is 'admin'. The decision is taken from the
 *    matched route segments, never the serialized URL, so matrix params
 *    (/admin;x=1) and auxiliary outlets (/admin(popup:x)) cannot skip it.
 * A denied persona is redirected to homeFor(persona), which is never a route
 * this guard denies for that persona.
 */
export function requirementForSegments(segments: ReadonlyArray<string>): PersonaRequirement | null {
    return segments[0] === 'admin' ? 'admin' : null;
}

/** Matched path segments from the root to this route (params and outlets excluded). */
export function matchedSegments(route: ActivatedRouteSnapshot): string[] {
    return (route.pathFromRoot ?? [route]).flatMap((r) => (r.url ?? []).map((u) => u.path));
}

/**
 * Pure decision, shared by the guard and its loop-proof test.
 * SPEC-web-planner-024 AC-7: 'cro' needs the CRO persona AND a workspace plan that offers CRO (`plan`), because a
 * plan downgrade keeps existing CRO members (ChangeClientPlanCommandHandler). A null or unknown plan offers every
 * role, as users-api SeatGuard does.
 */
export function isAllowed(req: PersonaRequirement | null, persona: Persona, superAdmin: boolean, plan?: string | null): boolean {
    switch (req) {
        case null: return true;
        case 'admin': return persona === 'admin' || superAdmin;
        case 'superadmin': return superAdmin;
        case 'is': return persona === 'is';
        case 'cro': {
            if (persona !== 'cro') return false;
            if (plan === undefined) return false; // the plan read result was not supplied: fail closed
            return planOffersCro(plan);
        }
    }
}

/**
 * Where a denied persona goes: its own home. The one exception is a cro refused the CRO pages (a plan without CRO, or a
 * failed User/Me): its own home IS those pages (/portfolio), so it goes to /home, which any member may open.
 */
export function deniedTarget(req: PersonaRequirement | null, persona: Persona, superAdminHere: boolean): string {
    return req === 'cro' && persona === 'cro' && !superAdminHere ? '/home' : homeFor(persona, superAdminHere);
}

/**
 * SPEC-web-planner-013 AC-87 (manager R125): may the viewer use an Admin-only write control (products today)?
 * Only a MEMBER of the selected workspace whose own entry maps to the Admin persona (Owner/Admin/Administrator).
 * `superAdmin` is deliberately not consulted: it is global, so an IS/FS/CRO here who is SuperAdmin elsewhere stays
 * read-only, and the platform SuperAdmin with no entry here (resolvePersona gives it persona 'admin', member false) is
 * read-only too (manager R125b: D-SA1 stands, the platform SuperAdmin does not write a client's products).
 * Known drift, not handled here: this file's persona-mapping still maps a 'SuperAdmin' role string to admin, while the
 * backend maps it to no persona (SPEC-web-planner-012 AC-4). An entry whose roles hold 'SuperAdmin' is superAdminHere,
 * and RoleGuard sends that user to /admin/subscription before any products page renders. No answer yet, or a 401,
 * is read-only, so a write control never flashes in. A failed User/Me resolves from the stored user (the backend's
 * [AdminOnly] stays the authority).
 */
export function canAdminWrite(resolved: Pick<ResolvedPersona, 'persona' | 'member' | 'unauthorized'> | null | undefined): boolean {
    return !!resolved && !resolved.unauthorized && resolved.member === true && resolved.persona === 'admin';
}

/**
 * AC-87: `canAdminWrite` as a stream for a page that shows workspace `workspaceId`'s data. Tri-state:
 * `null` = not known yet (render neither the write controls nor the read-only copy), `true` / `false` = the answer.
 *  - Re-resolves when the signed-in user changes (a role change; PersonaService drops its cache on the same event and
 *    subscribed first), emitting `null` until the new answer lands, so a demotion never leaves the old `true` up.
 *  - Once the selected workspace is no longer the page's own (`workspaceId`), the page is read-only: it still shows,
 *    and would write to, the old workspace's data, so the new workspace's persona does not apply to it.
 * Expiry of PersonaService's positive TTL is not watched: a mounted page keeps its answer until one of these events or
 * a reload; the backend's [AdminOnly] stays the authority for the write itself.
 */
export function adminWrite$(
    personaService: PersonaService,
    authService: { selectedTenant?: { id?: string | null } | null; selectedTenant$?: Observable<unknown>; user$?: Observable<unknown> },
    workspaceId: string | null | undefined
): Observable<boolean | null> {
    return resolvedPersonaFor$(personaService, authService, workspaceId).pipe(
        map((r) => (r === 'other' ? false : r === 'pending' ? null : canAdminWrite(r))),
        distinctUntilChanged()
    );
}

type PersonaAuth = { selectedTenant?: { id?: string | null } | null; selectedTenant$?: Observable<unknown>; user$?: Observable<unknown> };

/**
 * The viewer's resolved persona for a page that shows workspace `workspaceId`'s data, re-resolved when the user or
 * the selected workspace changes: `'pending'` until each answer lands, `'other'` once the selected workspace is not
 * the page's own. Shared by `adminWrite$` (AC-87) and `personaSection$` (AC-88), so the two never drift.
 */
function resolvedPersonaFor$(
    personaService: PersonaService,
    authService: PersonaAuth,
    workspaceId: string | null | undefined
): Observable<ResolvedPersona | 'pending' | 'other'> {
    return merge(of(null), authService.selectedTenant$ ?? EMPTY, authService.user$ ?? EMPTY).pipe(
        switchMap(() =>
            !workspaceId || authService.selectedTenant?.id !== workspaceId
                ? of('other' as const)
                : personaService.resolve().pipe(startWith('pending' as const))
        )
    );
}

/** The four persona sections, in the shell's `roleLabel` words. */
export type PersonaSectionLabel = 'Admin' | 'Inside Sales' | 'Field Sales' | 'CRO';

/**
 * SPEC-web-planner-013 AC-88: the viewer's own section for a breadcrumb ("<Org> / Inside Sales / Products"), or
 * `null` when it is not known (no answer, a 401, not a member, no persona), so a page never names another
 * persona's section.
 */
export function sectionLabel(resolved: Pick<ResolvedPersona, 'persona' | 'member' | 'unauthorized'> | null | undefined): PersonaSectionLabel | null {
    if (!resolved || resolved.unauthorized || resolved.member !== true) return null;
    switch (resolved.persona) {
        case 'admin': return 'Admin';
        case 'is':    return 'Inside Sales';
        case 'fs':    return 'Field Sales';
        case 'cro':   return 'CRO';
        default:      return null;
    }
}

/** AC-88: `sectionLabel` as a stream, re-resolved on the same events as `adminWrite$`. `null` until the first
 *  answer and at once when the selected workspace is no longer the page's own; while a re-resolve is pending it
 *  keeps the last section, so a profile update does not make the crumb jump (a label, not a permission). */
export function personaSection$(
    personaService: PersonaService,
    authService: PersonaAuth,
    workspaceId: string | null | undefined
): Observable<PersonaSectionLabel | null> {
    return resolvedPersonaFor$(personaService, authService, workspaceId).pipe(
        filter((r): r is ResolvedPersona | 'other' => r !== 'pending'),
        map((r) => (r === 'other' ? null : sectionLabel(r))),
        startWith(null as PersonaSectionLabel | null),
        distinctUntilChanged()
    );
}

@Injectable({ providedIn: 'root' })
export class PersonaGuard implements CanActivate, CanActivateChild {
    constructor(private _personaService: PersonaService, private _router: Router, private _injector: Injector) {}

    canActivate(route: ActivatedRouteSnapshot, _state?: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean {
        return this._check(this._requirementFor(route));
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, _state?: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean {
        return this._check(this._requirementFor(childRoute));
    }

    /** Route-level data wins (e.g. queue); the stubs mount has none and uses the matched segments. */
    private _requirementFor(route: ActivatedRouteSnapshot): PersonaRequirement | null {
        return route.data?.['persona'] ?? requirementForSegments(matchedSegments(route));
    }

    private _check(req: PersonaRequirement | null): Observable<boolean | UrlTree> | boolean {
        if (!req) {
            return true;
        }
        return this._personaService.resolve().pipe(
            switchMap(({ persona, superAdmin, superAdminHere, failed, unauthorized }): Observable<boolean | UrlTree> => {
                const deny = () => this._router.parseUrl(deniedTarget(req, persona, superAdminHere));
                if (unauthorized) { return of(false); } // the interceptor signs out and goes to /sign-in
                // User/Me failed: the backend remains the authority, except for the CRO pages, which have no
                // backend behind them yet (AC-7: fail closed).
                if (failed) { return of(req === 'cro' ? deny() : true); }
                if (req === 'cro' && persona === 'cro') {
                    // A failed or timed-out SeatLimit read (null) refuses: fail closed, like a failed User/Me. A
                    // successful read whose plan is null or unknown allows (SeatGuard treats it as Top).
                    return this._workspacePlan().pipe(map((r) => (r.read && isAllowed(req, persona, superAdmin, r.plan) ? true : deny())));
                }
                return of(isAllowed(req, persona, superAdmin) ? true : deny());
            })
        );
    }

    /** The selected workspace's plan (subscriptions-api SeatLimit). `read` is false when the read failed (or there is no workspace). */
    private _workspacePlan(): Observable<{ read: boolean; plan: string | null }> {
        const id = this._injector.get(AuthService).selectedTenant?.id;
        return id
            ? this._injector.get(WorkspaceService).getSeatLimit(id).pipe(map((d) => ({ read: !!d, plan: d?.plan ?? null })))
            : of({ read: false, plan: null });
    }
}
